<script lang="ts">
	import type { Navigation, NavigationItem } from '$models/Navigation/Navigation';
	import CheckIcon from '$components/UI/Icons/CheckIcon.svelte';
	import GlobeIcon from '$components/UI/Icons/GlobeIcon.svelte';
	import { getMobileMenuState } from '$lib/state/menu.svelte';
	import { getWeglotState } from '$lib/state/weglot.svelte';

	let {
		navigation,
		active
	}: {
		navigation: Navigation;
		active: boolean;
	} = $props();

	const mobileMenuState = getMobileMenuState();
	const weglot = getWeglotState();

	let activeSubmenuItem: NavigationItem | null = $derived(
		navigation.items.find((item) => item.id === mobileMenuState.activeSubmenuId()) ?? null
	);

	function closeMenu() {
		mobileMenuState.setActive(false);
	}

	function toggleLanguageSubmenu() {
		if (mobileMenuState.isLanguageSubmenuActive()) {
			mobileMenuState.closeSubmenu();
		} else {
			mobileMenuState.openLanguageSubmenu();
		}
	}

	function selectLanguage(code: string) {
		mobileMenuState.closeSubmenu();
		weglot.switchTo(code);
	}
</script>

{#snippet arrowRight()}
	<svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M8 8L6.9375 6.9375L9.125 4.75H0V3.25H9.125L6.9375 1.0625L8 0L12 4L8 8Z"
			fill="currentColor"
		/>
	</svg>
{/snippet}

{#snippet chevron()}
	<svg xmlns="http://www.w3.org/2000/svg" width="6" height="10" viewBox="0 0 8.91 16">
		<path
			d="m.33,15.67c.45.45,1.17.45,1.62,0l6.45-6.45c.67-.67.67-1.75,0-2.42L1.95.33c-.45-.45-1.17-.45-1.62,0-.45.45-.45,1.17,0,1.62l6.05,6.05L.33,14.05c-.45.45-.45,1.17,0,1.62Z"
			fill="currentColor"
		/>
	</svg>
{/snippet}

{#snippet checkMark()}
	<svg width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M4.04167 8.875L0 4.85417L1.0625 3.77083L4.04167 6.75L10.8125 0L11.875 1.0625L4.04167 8.875Z"
			fill="#4BE1A4"
		/>
	</svg>
{/snippet}

<div class="toggle-menu" class:is-active={active}>
	<div class="toggle-menu-panel toggle-menu-panel--main">
		<nav class="toggle-menu-nav">
			<ul>
				{#each navigation.items as item}
					{@const hasChildren = item.children && item.children.length > 0}
					<li>
						{#if hasChildren}
							<button
								type="button"
								class="toggle-menu-link"
								onclick={() => mobileMenuState.openSubmenu(item.id)}
							>
								<span>{item.title}</span>
								<span class="toggle-menu-link-icon">{@render chevron()}</span>
							</button>
						{:else}
							<a
								href={item.url}
								class={['toggle-menu-link', ...item.classes].join(' ')}
								target={item.target ?? '_self'}
								onclick={closeMenu}
							>
								{item.title}
							</a>
						{/if}
					</li>
				{/each}
			</ul>
		</nav>

		{#if weglot.hasAlternates()}
			<div class="toggle-menu-language" class:is-open={mobileMenuState.isLanguageSubmenuActive()}>
				<ul class="toggle-menu-language-dropdown">
					{#each weglot.languages() as code}
						{@const isCurrent = code === weglot.currentLanguage()}
						<li>
							<button
								type="button"
								class:is-current={isCurrent}
								onclick={() => selectLanguage(code)}
							>
								{weglot.languageName(code)}
								{#if isCurrent}
									<span class="toggle-menu-language-check">{@render checkMark()}</span>
								{/if}
							</button>
						</li>
					{/each}
				</ul>

				<button
					type="button"
					class="toggle-menu-language-trigger"
					aria-expanded={mobileMenuState.isLanguageSubmenuActive()}
					onclick={toggleLanguageSubmenu}
				>
					<span class="toggle-menu-language-trigger-icon"><GlobeIcon /></span>
					<span class="toggle-menu-language-trigger-label">
						{weglot.languageName(weglot.currentLanguage() ?? '')}
					</span>
					<span class="toggle-menu-language-trigger-chevron">{@render chevron()}</span>
				</button>
			</div>
		{/if}
	</div>

	<div class="toggle-menu-panel toggle-menu-panel--sub" class:is-active={!!activeSubmenuItem}>
		<button type="button" class="toggle-menu-back" onclick={() => mobileMenuState.closeSubmenu()}>
			<span class="toggle-menu-back-icon">{@render arrowRight()}</span>
			<span>Terug</span>
		</button>

		{#if activeSubmenuItem}
			<nav class="toggle-menu-nav">
				<ul>
					<li>
						<span class="toggle-menu-title">
							{activeSubmenuItem.title}
						</span>
					</li>
					{#each activeSubmenuItem.children as child}
						<li>
							<a
								href={child.url}
								class={['toggle-menu-sub-link', ...child.classes].join(' ')}
								target={child.target ?? '_self'}
								onclick={closeMenu}
							>
								{child.title}
								<span class="toggle-menu-sub-link-icon">{@render arrowRight()}</span>
							</a>
						</li>
					{/each}
				</ul>
			</nav>
		{/if}
	</div>
</div>

<style lang="postcss" global>
	.toggle-menu {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		overflow: hidden;
		opacity: 0;
		visibility: hidden;
		transition:
			opacity var(--transition) ease,
			visibility var(--transition) ease;

		&.is-active {
			opacity: 1;
			visibility: visible;
		}

		@media (--xl) {
			display: none;
		}
	}

	.toggle-menu-panel {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		display: flex;
		flex-direction: column;
		gap: 40px;
		padding: 90px 24px 40px;
		background-color: var(--color-white);
		overflow-y: auto;

		&--sub {
			gap: 0;
			z-index: 1;
			transform: translateX(100%);
			transition: transform var(--transition) ease;

			&.is-active {
				transform: translateX(0);
			}
		}
	}

	.ribbon + .header .toggle-menu-panel {
		padding-top: calc(72px + var(--ribbon-height, 40px));
	}

	.header.has-promotion-bar .toggle-menu-panel {
		padding-top: calc(72px + var(--promotion-bar-height, 74px));
	}

	.ribbon + .header.has-promotion-bar .toggle-menu-panel {
		padding-top: calc(72px + var(--ribbon-height, 40px) + var(--promotion-bar-height, 74px));
	}

	.toggle-menu-nav {
		padding-top: 60px;
		ul li {
			white-space: nowrap;
		}
	}

	.toggle-menu-title {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-3);
		width: 100%;
		margin-bottom: 16px;
		font: inherit;
		color: var(--color-brown);
		font-family: var(--toggle-menu-font-family);
		font-size: 20px;
		line-height: var(--toggle-menu-font-lineheight);
		text-transform: var(--toggle-menu-text-transform);
		font-weight: var(--toggle-menu-font-weight);
	}

	.toggle-menu-link {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-3);
		width: calc(100% + 16px);
		margin: 0;
		padding: 12px 8px;
		margin-inline: -8px;
		background: none;
		border: 0;
		font: inherit;
		text-align: left;
		border-radius: 8px;
		cursor: pointer;
		color: var(--color-brown);
		font-family: var(--toggle-menu-font-family);
		font-size: 20px;
		line-height: var(--toggle-menu-font-lineheight);
		text-transform: var(--toggle-menu-text-transform);
		font-weight: var(--toggle-menu-font-weight);

		&-icon {
			flex: 0 0 auto;
			display: flex;
		}

		&:hover {
			background-color: var(--color-primary-light);
			color: var(--color-brown);
		}
	}

	.toggle-menu-sub-link {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-3);
		width: calc(100% + 16px);
		margin: 0;
		padding: 12px 8px;
		margin-inline: -8px;
		background: none;
		border: 0;
		font: inherit;
		text-align: left;
		border-radius: 8px;
		cursor: pointer;
		color: var(--color-brown);
		font-size: 18px;
		font-family: var(--font-primary);
		line-height: 1.6;

		&-icon {
			opacity: 0;
			visibility: hidden;
			transform: translateX(-3px);
			transition:
				opacity var(--transition),
				visibility var(--transition),
				transform var(--transition);
		}

		&:hover {
			background-color: var(--color-primary-light);
			color: var(--color-brown);

			.toggle-menu-sub-link-icon {
				opacity: 1;
				visibility: visible;
				transform: translateX(0);
			}
		}
	}

	.toggle-menu-back {
		display: flex;
		align-items: center;
		gap: var(--spacing-2);
		flex: 0 0 auto;
		margin: 0;
		padding: 0;
		background: none;
		border: 0;
		cursor: pointer;
		color: var(--color-brown);

		&-icon {
			display: flex;
			width: 12px;
			transform: scaleX(-1);

			svg {
				width: 12px;
			}
		}
	}

	.toggle-menu-language {
		position: relative;
		flex: 0 0 auto;
		margin-top: auto;
	}

	.toggle-menu-language-trigger {
		display: flex;
		align-items: center;
		gap: var(--spacing-2);
		width: auto;
		margin: 0;
		padding: 8px 12px;
		background: none;
		border: 1px solid #e0ddd9;
		border-radius: 8px;
		cursor: pointer;
		color: var(--color-brown);
		font-family: var(--toggle-menu-font-family);
		font-size: 14px;
		text-transform: var(--toggle-menu-text-transform);
		font-weight: var(--toggle-menu-font-weight);

		&-icon {
			display: flex;
			flex: 0 0 auto;
		}

		&-chevron {
			display: flex;
			flex: 0 0 auto;
			margin-left: auto;
			transform: rotate(90deg);

			.toggle-menu-language.is-open & {
				transform: rotate(270deg);
			}
		}
	}

	.toggle-menu-language-dropdown {
		position: absolute;
		z-index: 1;
		bottom: calc(100% + 4px);
		left: 0;
		display: block;
		width: max-content;
		min-width: 220px;
		padding: 16px 12px;
		border-radius: 8px;
		background-color: var(--color-white);
		opacity: 0;
		visibility: hidden;
		transition:
			opacity var(--transition),
			visibility var(--transition);

		li {
			margin: 0;
			padding: 0;
			white-space: nowrap;
		}

		button {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: var(--spacing-3);
			width: 100%;
			padding: 8px 12px;
			border: 0;
			border-radius: 8px;
			cursor: pointer;
			color: var(--color-brown);
			font: inherit;
			font-size: 14px;
			font-family: var(--font-primary);
			line-height: 1.6;
			text-align: left;
			transition: background-color var(--transition);

			&:hover {
				background-color: var(--color-primary-light);
			}
		}

		.toggle-menu-language.is-open & {
			opacity: 1;
			visibility: visible;
			border: 1px solid #e0ddd9;
		}
	}

	.toggle-menu-language-check {
		display: flex;
		flex: 0 0 auto;
		width: 14px;
		color: var(--color-brown);
	}
</style>
